// journal-lease-owner-liveness.ts
//
// Phase 05 (AC-02, B1): decides whether `<control_root>/locks/journal.lock`'s
// owner pid can still be alive — same "lstart > acquiredAt ⇒ pid reused ⇒
// owner dead" rule as machine-lock pid-reuse detection, adapted for a lease
// that carries no start-time of its own (event-journal.ts's lease is just
// `{pid, acquiredAt}`). Kept in its own module so orphaned-supervisor-lock-
// release.ts stays under 200 lines.
import { readFile } from "node:fs/promises";
import type { MachineLockFile } from "./mission-lock.js";
import type { LockLivenessProbe } from "./process-liveness-probe.js";

export interface ClassifyJournalLeaseOwnerInput {
  leasePath: string;
  machineLock?: MachineLockFile;
  supervisorDead: boolean;
  liveness: LockLivenessProbe;
}

export interface JournalLeaseOwnerClassification {
  state: "absent" | "owner_dead" | "owner_alive_or_unknown";
  pid?: number;
}

interface JournalLease {
  pid: number;
  acquiredAt: string;
}

function isValidLease(v: unknown): v is JournalLease {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return Number.isInteger(r.pid) && (r.pid as number) > 1 && typeof r.acquiredAt === "string";
}

/**
 * (B1) lstart of the CURRENT pid holder, compared to when the lease was
 * acquired: a real owner always started BEFORE it wrote the lease
 * (lstart <= acquiredAt); lstart strictly AFTER acquiredAt means the pid was
 * reused by a later, unrelated process — the real owner is dead. Any
 * unparsable value (locale-garbled `ps` output, corrupt lease) resolves to
 * "alive or unknown" — fail-closed, never gỡ nhầm lease của chủ còn sống.
 */
export async function classifyJournalLeaseOwner(
  input: ClassifyJournalLeaseOwnerInput,
): Promise<JournalLeaseOwnerClassification> {
  let raw: unknown;
  try {
    raw = JSON.parse(await readFile(input.leasePath, "utf8"));
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === "ENOENT") return { state: "absent" };
    return { state: "owner_alive_or_unknown" };
  }
  if (!isValidLease(raw)) return { state: "owner_alive_or_unknown" };
  const { pid, acquiredAt } = raw;

  // (a) machine lock is the same pid ⇒ its already-decided fate applies.
  if (input.machineLock && input.machineLock.pid === pid) {
    return { state: input.supervisorDead ? "owner_dead" : "owner_alive_or_unknown", pid };
  }

  // (b) any other holder: dead pid ⇒ gone; alive pid ⇒ check for reuse.
  if (!(await input.liveness.isAlive(pid))) return { state: "owner_dead", pid };
  const lstart = await input.liveness.processStartedAt(pid);
  const a = Date.parse(lstart ?? "");
  const b = Date.parse(acquiredAt);
  if (Number.isNaN(a) || Number.isNaN(b)) return { state: "owner_alive_or_unknown", pid };
  return { state: a > b ? "owner_dead" : "owner_alive_or_unknown", pid };
}
